// assets/habi-slideshow.js

(function () {
  'use strict';

  const SELECTORS = {
    root:        '.habi-slideshow',
    stage:       '.habi-slideshow__stage',
    slide:       '.habi-slideshow__slide',
    thumb:       '.habi-slideshow__thumb',
    arrowPrev:   '.habi-slideshow__arrow--prev',
    arrowNext:   '.habi-slideshow__arrow--next',
    zoom:        '.habi-slideshow__zoom',
    videoBtn:    '.habi-video-btn',
  };

  const instances = new Map();

  class HabiSlideshow {
    constructor(el) {
      this.el        = el;
      this.sectionId = el.dataset.sectionId;
      this.slides    = [...el.querySelectorAll(SELECTORS.slide)];
      this.thumbs    = [...el.querySelectorAll(SELECTORS.thumb)];
      this.current   = 0;
      this.mediaMap  = this._loadVariantMediaMap();
      this._init();
    }

    _loadVariantMediaMap() {
      const el = document.getElementById(`HabiVariantMedia-${this.sectionId}`);
      if (!el) return {};
      try {
        const arr = JSON.parse(el.textContent);
        return arr.reduce((map, item) => { map[String(item.v)] = String(item.m); return map; }, {});
      } catch { return {}; }
    }

    _init() {
      this._activateSlide(0, false);
      this._initArrows();
      this._initThumbs();
      this._initSwipe();
      this._initVideoBtn();
      this._initZoom();
      this._initVariantSync();
    }

    // ── Slide activation ─────────────────────────────────────────────

    _activateSlide(index, animate = true) {
      const n = this.slides.length;
      if (!n) return;
      index = ((index % n) + n) % n;

      this.slides.forEach((s, i) => {
        const active = i === index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.thumbs.forEach((t, i) => {
        const active = i === index;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          t.scrollIntoView({ behavior: animate ? 'smooth' : 'auto', block: 'nearest', inline: 'center' });
        }
      });

      this.current = index;
    }

    _activateByMediaId(mediaId) {
      const idx = this.slides.findIndex(s => s.dataset.mediaId === String(mediaId));
      if (idx >= 0) this._activateSlide(idx);
    }

    // ── Navigation arrows ─────────────────────────────────────────────

    _initArrows() {
      const prev = this.el.querySelector(SELECTORS.arrowPrev);
      const next = this.el.querySelector(SELECTORS.arrowNext);
      prev?.addEventListener('click', () => this._activateSlide(this.current - 1));
      next?.addEventListener('click', () => this._activateSlide(this.current + 1));
    }

    // ── Thumbnail strip ───────────────────────────────────────────────

    _initThumbs() {
      this.thumbs.forEach((t, i) => {
        t.addEventListener('click', () => this._activateSlide(i));
      });
    }

    // ── Touch swipe ───────────────────────────────────────────────────

    _initSwipe() {
      const stage = this.el.querySelector(SELECTORS.stage);
      if (!stage) return;
      let startX = 0, startY = 0, startT = 0;
      stage.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startT = Date.now();
      }, { passive: true });
      stage.addEventListener('touchend', e => {
        const t  = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;

        // Horizontal swipe → prev/next.
        if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
          this._activateSlide(dx < 0 ? this.current + 1 : this.current - 1);
          return;
        }

        // Tap (little movement, quick): tap the left/right third to change image.
        // Arrows are hidden on mobile, so this is the on-image navigation.
        const dt = Date.now() - startT;
        if (dt < 400 && Math.abs(dx) < 12 && Math.abs(dy) < 12) {
          if (this._isInteractiveTarget(e.target)) return; // let zoom/play/buttons work
          const rect = stage.getBoundingClientRect();
          const x    = t.clientX - rect.left;
          const zone = rect.width * 0.32;
          if (x <= zone) this._activateSlide(this.current - 1);
          else if (x >= rect.width - zone) this._activateSlide(this.current + 1);
          // Center third: no navigation (reserved for zoom / video play).
        }
      }, { passive: true });
    }

    // Skip tap-navigation when the tap lands on an interactive element inside the
    // stage (zoom button, video play overlay, injected iframe/video, links).
    _isInteractiveTarget(target) {
      return !!(target && target.closest && target.closest(
        'button, a, input, textarea, select, video, iframe, [data-video-type], .habi-slideshow__play-overlay'
      ));
    }

    // ── Video button ──────────────────────────────────────────────────
    // Navigates to the first video slide and plays it.
    // Falls back to opening the configured URL.

    _initVideoBtn() {
      const btn = this.el.querySelector(SELECTORS.videoBtn);
      if (!btn) return;

      btn.addEventListener('click', () => {
        const firstVideoId = btn.dataset.firstVideoId;
        if (firstVideoId) {
          const idx = this.slides.findIndex(s => s.dataset.mediaId === firstVideoId);
          if (idx >= 0) {
            this._activateSlide(idx);
            this._playVideoInSlide(this.slides[idx]);
            return;
          }
        }
        const url = btn.dataset.videoUrl;
        if (url) window.open(url, '_blank', 'noopener noreferrer');
      });
    }

    _playVideoInSlide(slide) {
      const type = slide.dataset.mediaType;

      if (type === 'video') {
        const video = slide.querySelector('video');
        if (!video) return;
        if (!video.readyState) video.load();
        video.play().catch(() => {});
        return;
      }

      if (type === 'external_video') {
        const tpl = slide.querySelector('template[data-video-type]');
        if (!tpl) return;
        // Remove any previously injected iframe
        slide.querySelector('iframe')?.remove();
        const { videoType, videoId } = tpl.dataset;
        const iframe = document.createElement('iframe');
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        if (videoType === 'youtube') {
          iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
        } else if (videoType === 'vimeo') {
          iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&dnt=1`;
        } else {
          return;
        }
        // Hide poster/overlay
        slide.querySelector('.habi-slideshow__video-poster')?.style.setProperty('visibility', 'hidden');
        slide.querySelector('.habi-slideshow__play-overlay')?.style.setProperty('display', 'none');
        slide.appendChild(iframe);
      }
    }

    // ── Zoom ──────────────────────────────────────────────────────────
    // Opens the active slide's full-resolution image in a new tab.

    _initZoom() {
      const zoomBtn = this.el.querySelector(SELECTORS.zoom);
      if (!zoomBtn) return;
      zoomBtn.addEventListener('click', () => {
        const activeSlide = this.slides[this.current];
        const img = activeSlide?.querySelector('img');
        if (img) window.open(img.src, '_blank', 'noopener noreferrer');
      });
    }

    // ── Variant → media sync ──────────────────────────────────────────
    // When a variant is picked, switch the slideshow to that variant's
    // featured media (the variant "avatar" image).
    //
    // The old approach listened for a `change` event on the hidden
    // input[name="id"]. That is unreliable: the input only exists when the
    // buy-buttons block is enabled, and Dawn swaps in a fresh <variant-selects>
    // via viewTransition rather than dependably firing that change. So we use
    // the same trigger the section's inline perso/label code already relies on —
    // a MutationObserver that fires when Dawn inserts the new <variant-selects>,
    // then read the selected variant JSON (which includes featured_media).

    _initVariantSync() {
      this._productInfo =
        this.el.closest('product-info') ||
        document.getElementById(`MainProduct-${this.sectionId}`);
      if (!this._productInfo) return;

      // Observe the new <variant-selects> insertion (fires after Dawn's DOM swap,
      // covers the buy-buttons-disabled case too).
      this._variantObserver = new MutationObserver(mutations => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeName === 'VARIANT-SELECTS') { this._syncVariantMedia(); return; }
          }
        }
      });
      this._variantObserver.observe(this._productInfo, { childList: true, subtree: true });

      // Extra trigger for when buy_buttons is present (harmless duplicate — sync is idempotent).
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (form) {
        this._formChangeHandler = () => requestAnimationFrame(() => this._syncVariantMedia());
        form.addEventListener('change', this._formChangeHandler);
        this._syncForm = form;
      }

      // Reflect the initially-selected variant's media on load.
      this._syncVariantMedia();
    }

    // Read the currently selected variant from Dawn's JSON blob and jump to its
    // featured media. Prefers the variant's own featured_media.id; falls back to
    // the section's variant→media map.
    _syncVariantMedia() {
      const variant = this._getSelectedVariant();
      if (!variant) return;
      let mediaId = variant.featured_media && variant.featured_media.id;
      if (!mediaId) mediaId = this.mediaMap[String(variant.id)];
      if (mediaId) this._activateByMediaId(mediaId);
    }

    _getSelectedVariant() {
      const pi = this._productInfo;
      if (!pi) return null;
      // Dawn inserts the new <variant-selects> BEFORE the old one, so the first
      // match in document order is the freshly-selected variant.
      const el = pi.querySelector('variant-selects [data-selected-variant]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch { return null; }
    }

    destroy() {
      this._variantObserver?.disconnect();
      this._variantObserver = null;
      if (this._syncForm && this._formChangeHandler) {
        this._syncForm.removeEventListener('change', this._formChangeHandler);
        this._syncForm = null;
        this._formChangeHandler = null;
      }
    }
  }

  // ── Init / destroy ────────────────────────────────────────────────────

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new HabiSlideshow(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) {
        instances.get(el).destroy();
        instances.delete(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded',        () => init());
  document.addEventListener('shopify:section:load',    e  => init(e.target));
  document.addEventListener('shopify:section:unload',  e  => destroy(e.target));

})();
